import { decide, PATIENT_PHOTO_MIME_TYPES, type PatientPhotoMimeType } from '@emr/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { errorMessage } from '../../api/client.ts';
import { fetchPatientPhotoUrl, uploadPatientPhoto } from '../../api/patientPhoto.ts';
import { useGrants } from '../../auth/sessionContext.ts';

const ACCEPTED_MIME_TYPES: readonly string[] = PATIENT_PHOTO_MIME_TYPES;

function photoQueryKey(patientId: string) {
  return ['patientPhoto', patientId];
}

/**
 * Patient photo on the chart profile (`BACKLOG.md` C18) — a small thumbnail when one is stored, plus
 * an upload/replace control gated on `patient:write`, the same tier
 * `documents/patientPhotoRoutes.ts` gates the routes on (not `chart:write`, matching that route
 * file's own demographic-tier decision). Deliberately its own component, not folded into
 * `PatientChartPage.tsx`'s `PatientHeader`, matching this directory's own `ChartPresenceIndicator`
 * precedent for a self-contained header widget with its own query/mutation lifecycle.
 *
 * Capture is staff-workstation-only for v1 (`BACKLOG.md` C18's own decision) — no kiosk self-capture
 * here. "Webcam or file upload" is satisfied by a plain `<input type="file">`: most desktop and
 * mobile file pickers already offer a camera/photo-booth capture option from within that same
 * dialog, so this does not build a separate `getUserMedia` live-camera widget — a materially larger
 * piece of UI surface than this pass's scope, left as a named follow-up rather than attempted
 * partially.
 */
export function PatientPhoto({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('patientChart');
  const grants = useGrants();
  const mayWrite = decide(grants, 'patient:write');
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pickerError, setPickerError] = useState<string | null>(null);

  const photoQuery = useQuery({
    queryKey: photoQueryKey(patientId),
    queryFn: ({ signal }) => fetchPatientPhotoUrl(patientId, signal),
  });

  const upload = useMutation({
    mutationFn: ({
      file,
      declaredMimeType,
    }: {
      file: File;
      declaredMimeType: PatientPhotoMimeType;
    }) => uploadPatientPhoto(patientId, file, declaredMimeType),
    onSuccess: (response) => {
      queryClient.setQueryData(photoQueryKey(patientId), response.url);
    },
  });

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Always clear the input's own value, success or failure — otherwise picking the identical file
    // a second time (to retry after a refused upload) fires no `change` event at all.
    event.target.value = '';
    if (file === undefined) return;

    setPickerError(null);
    upload.reset();

    const declaredMimeType = ACCEPTED_MIME_TYPES.find((mimeType) => mimeType === file.type);
    if (declaredMimeType === undefined) {
      setPickerError(t('photo.unsupportedType'));
      return;
    }

    upload.mutate({ file, declaredMimeType: declaredMimeType as PatientPhotoMimeType });
  }

  const url = photoQuery.data ?? null;

  return (
    <div className="flex flex-col items-center gap-2">
      {url === null ? (
        <div
          className="flex h-20 w-20 items-center justify-center rounded-full bg-raised text-xs text-ink-muted"
          role="img"
          aria-label={t('photo.noPhotoAlt')}
        >
          {t('photo.noPhotoShort')}
        </div>
      ) : (
        <img src={url} alt={t('photo.photoAlt')} className="h-20 w-20 rounded-full object-cover" />
      )}
      {mayWrite ? (
        <>
          <button
            type="button"
            className="text-xs font-medium text-accent-text hover:underline"
            onClick={() => fileInputRef.current?.click()}
            disabled={upload.isPending}
          >
            {upload.isPending
              ? t('photo.uploading')
              : url === null
                ? t('photo.uploadAction')
                : t('photo.replaceAction')}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_MIME_TYPES.join(',')}
            className="sr-only"
            aria-label={url === null ? t('photo.uploadAction') : t('photo.replaceAction')}
            onChange={handleFileChange}
          />
          {pickerError !== null ? (
            <p className="m-0 max-w-32 text-center text-xs text-danger">{pickerError}</p>
          ) : upload.isError ? (
            <p className="m-0 max-w-32 text-center text-xs text-danger">
              {errorMessage(upload.error)}
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
